import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeftRight,
  ArrowDown,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { C } from "./profileTheme";
import useAifPrice from "@/hooks/api/useAifPrice";
import { CCC_AIF_ADDRESS, CCC_USDT_ADDRESS, USDT_ADDRESS } from "@/config/env";
import { getTokenName } from "@/config/common";
import type { BalanceItem } from "@/api/types";
import {
  formatEtherWithDecimals,
  formatNumberInput,
  formatThousands,
  formatTinyDecimal,
} from "@/lib/utils";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useConnection } from "wagmi";
import { signMessage } from "@wagmi/core";
import { parseEther } from "viem";
import { wagmiConfig } from "@/config";
import { useUserStore } from "@/store/useUserStore";
import {
  useBalanceTskSwapUsdt,
  useBalanceUsdtSwapTsk,
} from "@/hooks/api/useBalanceSwap";
import { showSuccess } from "@/lib/toast";

const FEE_RATE = 0.01;

export function SwapDialog({
  open,
  onClose,
  usdtBalanceData,
  aifBalanceData,
}: {
  open: boolean;
  onClose: () => void;
  usdtBalanceData?: BalanceItem | null;
  aifBalanceData?: BalanceItem | null;
}) {
  const { t } = useTranslation();
  const { address, connector } = useConnection();
  const { userInfo } = useUserStore();
  const usdtToTskSwap = useBalanceUsdtSwapTsk();
  const tskToUsdtSwap = useBalanceTskSwapUsdt();
  const { data: aifPriceApi } = useAifPrice();
  const tskPrice = aifPriceApi ?? 0;

  const usdtSymbol = getTokenName(USDT_ADDRESS);
  const tskSymbol = getTokenName(CCC_AIF_ADDRESS);

  const [direction, setDirection] = useState<"buy" | "sell">("buy");
  const [amount, setAmount] = useState("");
  const [step, setStep] = useState<"form" | "pending" | "success">("form");
  const [successText, setSuccessText] = useState("");

  const usdtAvailable = Number(
    formatEtherWithDecimals(BigInt(usdtBalanceData?.canUseUsdtAmount ?? "0"))
  );
  const aifAvailable = Number(
    formatEtherWithDecimals(BigInt(aifBalanceData?.total ?? "0"))
  );

  const numAmount = parseFloat(amount) || 0;
  const fee = numAmount * FEE_RATE;
  const netAmount = numAmount - fee;

  const tskReceived = direction === "buy" ? netAmount / tskPrice : 0;
  const usdtReceived = direction === "sell" ? netAmount * tskPrice : 0;

  const fromAsset = direction === "buy" ? usdtSymbol : tskSymbol;
  const toAsset = direction === "buy" ? tskSymbol : usdtSymbol;
  const fromBalance = direction === "buy" ? usdtAvailable : aifAvailable;
  const receiveAmount = direction === "buy" ? tskReceived : usdtReceived;
  const receiveFormatted =
    direction === "buy"
      ? `${receiveAmount.toFixed(2)} ${tskSymbol}`
      : `${formatThousands(usdtReceived)} ${usdtSymbol}`;
  const feeFormatted = `${fee.toFixed(2)} ${fromAsset}`;
  const insufficientBalance = numAmount > fromBalance;
  const isSubmitting = usdtToTskSwap.isPending || tskToUsdtSwap.isPending;

  const handleClose = () => {
    setStep("form");
    setAmount("");
    setSuccessText("");
    onClose();
  };

  const handleSwapDirection = () => {
    setDirection(d => (d === "buy" ? "sell" : "buy"));
    setAmount("");
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value && !/^\d*\.?\d*$/.test(value)) return;
    const [formatted] = formatNumberInput(value, fromBalance);
    setAmount(formatted);
  };

  const setQuickPct = (pct: number) => {
    const raw = (fromBalance * pct) / 100;
    const floored = Math.floor(raw * 100) / 100;
    setAmount(String(floored));
  };

  const handleConfirmSwap = async () => {
    if (!address) {
      toast.error(t("toast.common.connectWalletFirst"));
      return;
    }
    if (!userInfo?.uid) {
      toast.error(t("toast.swap.uidMissing"));
      return;
    }

    let amountWei: bigint;
    try {
      amountWei = parseEther(amount.trim() as `${number}`);
    } catch {
      toast.error(t("toast.swap.amountInvalid"));
      return;
    }
    if (amountWei <= 0n) {
      toast.error(t("toast.swap.amountInvalid"));
      return;
    }

    const signTick = Date.now();
    const orderId = String(signTick);

    const directionKey = direction === "buy" ? "usdt_to_tsk" : "tsk_to_usdt";
    const message = [
      "FortAI balance swap",
      directionKey,
      userInfo.uid,
      address.toLowerCase(),
      amountWei.toString(),
      "0",
      orderId,
      String(signTick),
    ].join(":");
    console.log("message", message);
    setStep("pending");
    let signature = "";
    try {
      signature = await signMessage(wagmiConfig, {
        connector,
        message,
      });
    } catch {
      setStep("form");
      toast.error(t("toast.swap.signRejected"));
      return;
    }

    try {
      const req = {
        orderId,
        amount: amountWei.toString(),
        minOut: "0",
        signTick,
        signature,
      };
      const res =
        direction === "buy"
          ? await usdtToTskSwap.mutateAsync(req)
          : await tskToUsdtSwap.mutateAsync(req);

      if (res.resultCode === 0) {
        setSuccessText(
          direction === "buy"
            ? `${formatThousands(numAmount)} ${usdtSymbol} → ${receiveAmount.toFixed(2)} ${tskSymbol}`
            : `${numAmount.toFixed(2)} ${tskSymbol} → ${formatThousands(usdtReceived)} ${usdtSymbol}`
        );
        setStep("success");
        showSuccess(
          t(
            direction === "buy"
              ? "toast.swap.usdtToTskAccepted"
              : "toast.swap.tskToUsdtAccepted"
          )
        );
        return;
      }

      setStep("form");
    } catch {
      setStep("form");
      toast.error(t("toast.swap.failed"));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next) handleClose();
      }}
    >
      <DialogContent
        className="max-w-sm mx-auto"
        onOpenAutoFocus={e => e.preventDefault()}
        style={{
          background: "#0d0f14",
          border: "1px solid rgba(245,158,11,0.25)",
          color: C.text,
        }}
      >
        <DialogHeader>
          <DialogTitle
            className="flex items-center gap-2"
            style={{ color: C.amber }}
          >
            <ArrowLeftRight size={18} />
            兑换
          </DialogTitle>
        </DialogHeader>
        <AnimatePresence mode="wait">
          {step === "form" && (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-4"
            >
              <div
                className="flex rounded-lg overflow-hidden"
                style={{ border: `1px solid ${C.border}` }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setDirection("buy");
                    setAmount("");
                  }}
                  className="flex-1 py-2.5 text-sm font-bold transition-all"
                  style={{
                    background:
                      direction === "buy"
                        ? "rgba(34,197,94,0.12)"
                        : "transparent",
                    color: direction === "buy" ? C.green : C.dim,
                    borderRight: `1px solid ${C.border}`,
                  }}
                >
                  买入 {tskSymbol}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDirection("sell");
                    setAmount("");
                  }}
                  className="flex-1 py-2.5 text-sm font-bold transition-all"
                  style={{
                    background:
                      direction === "sell"
                        ? "rgba(239,68,68,0.12)"
                        : "transparent",
                    color: direction === "sell" ? C.red : C.dim,
                  }}
                >
                  卖出 {tskSymbol}
                </button>
              </div>

              <div
                className="rounded-lg p-3 text-xs space-y-2"
                style={{ background: C.card, border: `1px solid ${C.border}` }}
              >
                {[
                  [`${tskSymbol} 当前价格`, `$${formatTinyDecimal(tskPrice)}`],
                  [
                    "手续费",
                    `${(FEE_RATE * 100).toFixed(0)}%（${direction === "buy" ? "买入" : "卖出"}）`,
                  ],
                  [
                    `可用 ${fromAsset}`,
                    `${direction === "buy" ? formatThousands(fromBalance) : fromBalance.toFixed(2)} ${fromAsset}`,
                  ],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between">
                    <span style={{ color: C.dim }}>{k}</span>
                    <span className="mono-num">{v}</span>
                  </div>
                ))}
              </div>

              <div>
                <p className="text-xs mb-2" style={{ color: C.dim }}>
                  支付数量
                </p>
                <div className="relative">
                  <input
                    type="number"
                    value={amount}
                    onChange={handleAmountChange}
                    placeholder={`输入 ${fromAsset} 数量`}
                    className="w-full px-3 py-3 rounded-lg text-sm mono-num outline-none"
                    style={{
                      background: C.card,
                      border: `1px solid ${insufficientBalance ? C.red : C.border}`,
                      color: C.text,
                    }}
                  />
                  <span
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold"
                    style={{ color: C.dim }}
                  >
                    {fromAsset}
                  </span>
                </div>
                {insufficientBalance && (
                  <p
                    className="text-[10px] mt-1 flex items-center gap-1"
                    style={{ color: C.red }}
                  >
                    <AlertTriangle size={10} /> 余额不足
                  </p>
                )}
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {[25, 50, 75, 100].map(pct => (
                    <button
                      key={pct}
                      type="button"
                      onClick={() => setQuickPct(pct)}
                      className="py-1.5 rounded-lg text-[11px] font-semibold transition-all hover:opacity-80 active:scale-95"
                      style={{
                        background: "rgba(255,255,255,0.06)",
                        border: `1px solid ${C.border}`,
                        color: C.muted,
                      }}
                    >
                      {pct === 100 ? "全部" : `${pct}%`}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-center -my-1">
                <button
                  type="button"
                  onClick={handleSwapDirection}
                  className="w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110 active:scale-95"
                  style={{
                    background: "rgba(245,158,11,0.12)",
                    border: "1px solid rgba(245,158,11,0.30)",
                  }}
                >
                  <ArrowDown size={14} style={{ color: C.amber }} />
                </button>
              </div>

              <div
                className="rounded-lg p-3"
                style={{ background: C.card, border: `1px solid ${C.border}` }}
              >
                <p className="text-xs mb-1" style={{ color: C.dim }}>
                  获得
                </p>
                <div className="flex items-center justify-between">
                  <span
                    className="mono-num text-lg font-bold"
                    style={{ color: direction === "buy" ? C.green : C.amber }}
                  >
                    {numAmount > 0
                      ? direction === "buy"
                        ? receiveAmount.toFixed(2)
                        : formatThousands(usdtReceived)
                      : "0.00"}
                  </span>
                  <span className="text-sm font-bold" style={{ color: C.dim }}>
                    {toAsset}
                  </span>
                </div>
              </div>

              {numAmount > 0 && (
                <div
                  className="rounded-lg p-3 text-xs space-y-1.5"
                  style={{
                    background: "rgba(245,158,11,0.04)",
                    border: "1px solid rgba(245,158,11,0.15)",
                  }}
                >
                  <div className="flex justify-between">
                    <span style={{ color: C.dim }}>支付 {fromAsset}</span>
                    <span className="mono-num" style={{ color: C.red }}>
                      -
                      {direction === "buy"
                        ? formatThousands(numAmount)
                        : numAmount.toFixed(2)}{" "}
                      {fromAsset}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: C.dim }}>
                      手续费（{(FEE_RATE * 100).toFixed(0)}%）
                    </span>
                    <span className="mono-num" style={{ color: C.amber }}>
                      -{feeFormatted}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: C.dim }}>实际获得 {toAsset}</span>
                    <span className="mono-num" style={{ color: C.green }}>
                      +{receiveFormatted}
                    </span>
                  </div>
                </div>
              )}

              <button
                type="button"
                className="w-full py-3 rounded-lg font-bold text-sm"
                disabled={
                  !amount ||
                  numAmount <= 0 ||
                  insufficientBalance ||
                  isSubmitting
                }
                style={{
                  background:
                    direction === "buy"
                      ? "rgba(34,197,94,0.15)"
                      : "rgba(239,68,68,0.15)",
                  border: `1px solid ${
                    direction === "buy"
                      ? "rgba(34,197,94,0.35)"
                      : "rgba(239,68,68,0.35)"
                  }`,
                  color: direction === "buy" ? C.green : C.red,
                  opacity:
                    !amount ||
                    numAmount <= 0 ||
                    insufficientBalance ||
                    isSubmitting
                      ? 0.4
                      : 1,
                }}
                onClick={() => void handleConfirmSwap()}
              >
                {isSubmitting
                  ? "提交中..."
                  : direction === "buy"
                    ? `确认买入 ${tskSymbol}`
                    : `确认卖出 ${tskSymbol}`}
              </button>
            </motion.div>
          )}
          {step === "pending" && (
            <motion.div
              key="pending"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center py-8 gap-3"
            >
              <RefreshCw
                size={28}
                className="animate-spin"
                style={{ color: C.amber }}
              />
              <p className="text-sm" style={{ color: C.muted }}>
                处理中...
              </p>
            </motion.div>
          )}
          {step === "success" && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center py-6 gap-3"
            >
              <CheckCircle size={40} style={{ color: C.green }} />
              <p className="font-semibold">兑换成功！</p>
              <p className="text-sm" style={{ color: C.muted }}>
                {successText}
              </p>
              <p className="text-xs" style={{ color: C.amber }}>
                手续费：{feeFormatted}
              </p>
              <button
                type="button"
                className="w-full py-3 rounded-lg font-bold text-sm btn-primary"
                onClick={handleClose}
              >
                完成
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
